import React from 'react';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  Box,
  Typography,
  FormControl,
  FormLabel,
} from '@mui/material';

/**
 * Design System Color Constants
 */
const COLORS = {
  ACCENT: '#005e5d',
  ACCENT_DARK: '#004d4c',
  ACCENT_LIGHT: '#005e5d14',
  SURFACE_SECONDARY: '#F2F3F5',
  BORDER: '#E6E7EB',
  TEXT_PRIMARY: '#333333',
  TEXT_SECONDARY: '#666666',
  TEXT_TERTIARY: '#999999',
  DESTRUCTIVE: '#D32F2F',
  SUCCESS: '#49CC90',
};

export interface RadioOption {
  label: string;
  value: string | number;
  description?: string;
  disabled?: boolean;
}

export interface BaseRadioProps {
  /**
   * List of radio options
   */
  options: RadioOption[];
  /**
   * Current selected value
   */
  value: string | number | undefined;
  /**
   * Change handler
   */
  onChange?: (value: string | number) => void;
  /**
   * Label for the radio group
   */
  label?: string;
  /**
   * Helper text displayed below
   */
  helperText?: string;
  /**
   * Layout direction
   */
  direction?: 'row' | 'column';
  /**
   * Is field required
   */
  required?: boolean;
  /**
   * Show error state
   */
  error?: boolean;
  /**
   * Disable all options
   */
  disabled?: boolean;
  /**
   * Show descriptions below labels
   */
  showDescriptions?: boolean;
  /**
   * Spacing between options
   */
  spacing?: number;
}

/**
 * BaseRadio Component
 *
 * Premium radio button component following the Pulse API design system.
 * Supports single selection with flexible option configuration.
 *
 * @example
 * ```tsx
 * // Basic radio group
 * <BaseRadio
 *   label="Choose option"
 *   options={[
 *     { label: 'Option 1', value: 'opt1' },
 *     { label: 'Option 2', value: 'opt2' },
 *   ]}
 *   value={selected}
 *   onChange={(val) => setSelected(val)}
 * />
 *
 * // With descriptions
 * <BaseRadio
 *   label="Subscription Plan"
 *   options={[
 *     { 
 *       label: 'Free',
 *       value: 'free',
 *       description: 'Basic features'
 *     },
 *     { 
 *       label: 'Pro',
 *       value: 'pro',
 *       description: 'Advanced features'
 *     },
 *   ]}
 *   value={plan}
 *   onChange={setPlan}
 *   showDescriptions
 * />
 *
 * // Horizontal layout
 * <BaseRadio
 *   label="Size"
 *   direction="row"
 *   options={sizeOptions}
 *   value={size}
 *   onChange={setSize}
 * />
 * ```
 */
export const BaseRadio: React.FC<BaseRadioProps> = ({
  options,
  value,
  onChange,
  label,
  helperText,
  direction = 'column',
  required = false,
  error = false,
  disabled = false,
  showDescriptions = false,
  spacing = 1.5,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    // Try to convert to number if it looks like a number
    const numValue = isNaN(Number(newValue)) ? newValue : Number(newValue);
    onChange?.(numValue);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <FormControl
        disabled={disabled}
        error={error}
        component="fieldset"
        required={required}
      >
        {label && (
          <FormLabel
            sx={{
              color: error ? COLORS.DESTRUCTIVE : COLORS.TEXT_PRIMARY,
              fontWeight: 600,
              fontSize: '13px',
              mb: 1,
              fontFamily: '"Manrope", sans-serif',

              '&.Mui-focused': {
                color: COLORS.ACCENT,
              },
            }}
          >
            {label}
            {required && <span style={{ color: COLORS.DESTRUCTIVE }}>*</span>}
          </FormLabel>
        )}

        <RadioGroup
          value={value ?? ''}
          onChange={handleChange}
          sx={{
            display: 'flex',
            flexDirection: direction,
            gap: spacing,
          }}
        >
          {options.map((option, index) => (
            <FormControlLabel
              key={index}
              value={option.value}
              disabled={option.disabled || disabled}
              control={
                <Radio
                  sx={{
                    color: COLORS.BORDER,
                    '&.Mui-checked': {
                      color: COLORS.ACCENT,
                    },
                    '&.Mui-disabled': {
                      color: COLORS.SURFACE_SECONDARY,
                    },
                    transition: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              }
              label={
                <Box sx={{ ml: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: option.disabled ? 400 : 500,
                      color: option.disabled ? COLORS.TEXT_TERTIARY : COLORS.TEXT_PRIMARY,
                      fontFamily: '"Manrope", sans-serif',
                    }}
                  >
                    {option.label}
                  </Typography>
                  {showDescriptions && option.description && (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: COLORS.TEXT_TERTIARY,
                        fontWeight: 400,
                        mt: 0.25,
                        fontFamily: '"Manrope", sans-serif',
                      }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                mb: 0,
                '&:hover': {
                  backgroundColor: !disabled ? COLORS.SURFACE_SECONDARY : 'transparent',
                  borderRadius: '8px',
                  transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                },
              }}
            />
          ))}
        </RadioGroup>

        {helperText && (
          <FormHelperText
            sx={{
              color: error ? COLORS.DESTRUCTIVE : COLORS.TEXT_TERTIARY,
              fontSize: '11px',
              fontWeight: 500,
              mt: 1,
              fontFamily: '"Manrope", sans-serif',
            }}
          >
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

BaseRadio.displayName = 'BaseRadio';

export default BaseRadio;
